import { useEffect, useMemo, useRef } from "react";

/** Invalidates work immediately when its account/resource changes, and on cleanup. */
export function useRequestScope(key: string, dependency?: unknown) {
  const current = useRef({ key, dependency, generation: 0, active: true });
  if (current.current.key !== key || current.current.dependency !== dependency) {
    current.current = { key, dependency, generation: current.current.generation + 1, active: true };
  }
  const owner = current.current;
  const scope = useMemo(() => {
    const requests = new Map<string, object>();
    const capture = () => {
      const generation = owner.generation;
      return () => current.current === owner && owner.active && owner.generation === generation;
    };
    return {
      capture,
      start(name: string) {
        const token = {};
        requests.set(name, token);
        const active = capture();
        return () => active() && requests.get(name) === token;
      },
    };
  }, [owner]);
  useEffect(() => {
    const owner = current.current;
    owner.active = true;
    return () => { owner.active = false; owner.generation += 1; };
  }, [scope]);
  return scope;
}
